// src/features/Users/userSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/api';

// Async Thunks for Clients
export const fetchClients = createAsyncThunk('users/fetchClients', async ({ page = 1, per_page = 10, status = '' }, { rejectWithValue }) => {
  try {
    const response = await api.get('/clients', {
      params: { page, per_page, status }, // Send pagination and filtering parameters
    });
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch clients');
  }
});

export const createClient = createAsyncThunk('users/createClient', async (clientData, { rejectWithValue }) => {
  try {
    const response = await api.post('/clients', clientData);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create client');
  }
});

export const updateClient = createAsyncThunk('users/updateClient', async ({ id, ...clientData }, { rejectWithValue }) => {
  try {
    const response = await api.post(`/clients/${id}`, {
      ...clientData,
      _method: 'PATCH',
    });
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Update failed');
  }
});

export const deleteClient = createAsyncThunk('users/deleteClient', async (id, { rejectWithValue }) => {
  try {
    await api.delete(`/clients/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Delete failed');
  }
});

// Async Thunks for Admins
export const fetchAdmins = createAsyncThunk(
  'users/fetchAdmins',
  async ({ page = 1, per_page = 10, status = '' }, { rejectWithValue }) => {
    try {
      const response = await api.get('/admins', {
        params: { page, per_page, status }, // Send pagination and filtering parameters
      });
      return response.data.data; // Return the paginated data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch admins');
    }
  }
);

export const createAdmin = createAsyncThunk('users/createAdmin', async (adminData, { rejectWithValue }) => {
  try {
    const response = await api.post('/admins', adminData);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create admin');
  }
});

export const updateAdmin = createAsyncThunk('users/updateAdmin', async ({ id, ...adminData }, { rejectWithValue }) => {
  try {
    const response = await api.post(`/admins/${id}`, {
      ...adminData,
      _method: 'PATCH',
    });
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Update failed');
  }
});

export const deleteAdmin = createAsyncThunk('users/deleteAdmin', async (id, { rejectWithValue }) => {
  try {
    await api.delete(`/admins/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Delete failed');
  }
});

export const fetchAcceptedClients = createAsyncThunk('users/fetchAcceptedClients', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/clients/accepted');
    return response.data.data; // Make sure the response structure matches
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch accepted clients');
  }
});

const userSlice = createSlice({
  name: 'users',
  initialState: {
    clients: {
      data: [], // Array of clients
      current_page: 1,
      per_page: 10,
      total: 0,
    },
    acceptedClients: [], // Should be a flat array of client objects
    admins: {
      data: [], // Array of admins
      current_page: 1,
      per_page: 10,
      total: 0,
    },
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Clients
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        
        state.loading = false;
        state.clients = action.payload;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAcceptedClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAcceptedClients.fulfilled, (state, action) => {
        state.loading = false;
        state.acceptedClients = action.payload;
      })
      .addCase(fetchAcceptedClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createClient.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClient.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteClient.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Admins
      .addCase(fetchAdmins.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = action.payload;
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.loading = false;
        // state.admins.push(action.payload);
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdmin.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        state.loading = false;
        // state.admins = state.admins.filter((a) => a.id !== action.payload);
      })
      .addCase(deleteAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default userSlice.reducer;